import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BookOpenIcon,
  BuildingOffice2Icon,
  ChevronRightIcon,
  BeakerIcon,
  ClockIcon,
  DocumentTextIcon,
  SparklesIcon,
  Square3Stack3DIcon,
  ShieldCheckIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import RoleOnly from "../../../routes/roleOnly";

export const UserGuideHub = () => {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = `${t("user_guide.hub.title")} | Promang`;
  }, [t]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 rounded-[2rem] border border-slate-200 bg-white px-6 py-6 shadow-sm">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
            <BookOpenIcon className="h-4 w-4" />
            {t("user_guide.hub.badge")}
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-950">{t("user_guide.hub.title")}</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">{t("user_guide.hub.subtitle")}</p>
        </div>

        <div className="grid gap-2 sm:gap-4">
          <Link
            to="/dashboard/guide/profile"
            className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 sm:gap-4 sm:rounded-2xl sm:px-5 sm:py-4"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-800 sm:h-12 sm:w-12 sm:rounded-xl">
              <UserCircleIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-slate-900 sm:text-sm">
                {t("user_guide.hub.card.profile.title")}
              </div>
              <div className="mt-0.5 text-[11px] leading-snug text-slate-600 sm:text-xs">
                {t("user_guide.hub.card.profile.description")}
              </div>
            </div>
            <ChevronRightIcon className="h-5 w-5 shrink-0 text-slate-400" />
          </Link>
          <RoleOnly roles={["drawings", "drawings_edit"]}>
            <Link
              to="/dashboard/guide/drawings"
              className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 sm:gap-4 sm:rounded-2xl sm:px-5 sm:py-4"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-800 sm:h-12 sm:w-12 sm:rounded-xl">
                <Square3Stack3DIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-slate-900 sm:text-sm">
                  {t("user_guide.hub.card.drawings.title")}
                </div>
                <div className="mt-0.5 text-[11px] leading-snug text-slate-600 sm:text-xs">
                  {t("user_guide.hub.card.drawings.description")}
                </div>
              </div>
              <ChevronRightIcon className="h-5 w-5 shrink-0 text-slate-400" />
            </Link>
          </RoleOnly>
          <RoleOnly role="admin">
            <Link
              to="/dashboard/guide/company"
              className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 sm:gap-4 sm:rounded-2xl sm:px-5 sm:py-4"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-800 sm:h-12 sm:w-12 sm:rounded-xl">
                <BuildingOffice2Icon className="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-slate-900 sm:text-sm">
                  {t("user_guide.hub.card.company.title")}
                </div>
                <div className="mt-0.5 text-[11px] leading-snug text-slate-600 sm:text-xs">
                  {t("user_guide.hub.card.company.description")}
                </div>
              </div>
              <ChevronRightIcon className="h-5 w-5 shrink-0 text-slate-400" />
            </Link>
          </RoleOnly>
          <RoleOnly role="admin">
            <Link
              to="/dashboard/ai/skills"
              className="flex items-center gap-2.5 rounded-xl border border-violet-200 bg-white px-3 py-2.5 shadow-sm transition hover:border-violet-300 hover:bg-violet-50/40 sm:gap-4 sm:rounded-2xl sm:px-5 sm:py-4"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-800 sm:h-12 sm:w-12 sm:rounded-xl">
                <SparklesIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-slate-900 sm:text-sm">
                  {t("ai_skills.hub_card.title")}
                </div>
                <div className="mt-0.5 text-[11px] leading-snug text-slate-600 sm:text-xs">
                  {t("ai_skills.hub_card.description")}
                </div>
              </div>
              <ChevronRightIcon className="h-5 w-5 shrink-0 text-slate-400" />
            </Link>
          </RoleOnly>
          <RoleOnly role="admin">
            <Link
              to="/dashboard/guide/chemical-inventory"
              className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 sm:gap-4 sm:rounded-2xl sm:px-5 sm:py-4"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-800 sm:h-12 sm:w-12 sm:rounded-xl">
                <BeakerIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-slate-900 sm:text-sm">
                  {t("user_guide.hub.card.chemical_inventory.title")}
                </div>
                <div className="mt-0.5 text-[11px] leading-snug text-slate-600 sm:text-xs">
                  {t("user_guide.hub.card.chemical_inventory.description")}
                </div>
              </div>
              <ChevronRightIcon className="h-5 w-5 shrink-0 text-slate-400" />
            </Link>
          </RoleOnly>
          <RoleOnly role="admin">
            <Link
              to="/dashboard/guide/hse-ks"
              className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 sm:gap-4 sm:rounded-2xl sm:px-5 sm:py-4"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-800 sm:h-12 sm:w-12 sm:rounded-xl">
                <ShieldCheckIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-slate-900 sm:text-sm">
                  {t("user_guide.hub.card.hse.title")}
                </div>
                <div className="mt-0.5 text-[11px] leading-snug text-slate-600 sm:text-xs">
                  {t("user_guide.hub.card.hse.description")}
                </div>
              </div>
              <ChevronRightIcon className="h-5 w-5 shrink-0 text-slate-400" />
            </Link>
          </RoleOnly>
          <RoleOnly role="admin">
            <Link
              to="/dashboard/guide/offers"
              className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 sm:gap-4 sm:rounded-2xl sm:px-5 sm:py-4"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-800 sm:h-12 sm:w-12 sm:rounded-xl">
                <DocumentTextIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-slate-900 sm:text-sm">
                  {t("user_guide.hub.card.offers.title")}
                </div>
                <div className="mt-0.5 text-[11px] leading-snug text-slate-600 sm:text-xs">
                  {t("user_guide.hub.card.offers.description")}
                </div>
              </div>
              <ChevronRightIcon className="h-5 w-5 shrink-0 text-slate-400" />
            </Link>
          </RoleOnly>
          <RoleOnly role="admin">
            <Link
              to="/dashboard/guide/hours"
              className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 sm:gap-4 sm:rounded-2xl sm:px-5 sm:py-4"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-800 sm:h-12 sm:w-12 sm:rounded-xl">
                <ClockIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-slate-900 sm:text-sm">
                  {t("user_guide.hub.card.hours.title")}
                </div>
                <div className="mt-0.5 text-[11px] leading-snug text-slate-600 sm:text-xs">
                  {t("user_guide.hub.card.hours.description")}
                </div>
              </div>
              <ChevronRightIcon className="h-5 w-5 shrink-0 text-slate-400" />
            </Link>
          </RoleOnly>
        </div>

        <p className="mt-8 text-center text-xs text-slate-500">{t("user_guide.hub.footer_note")}</p>
      </div>
    </div>
  );
};
